<script setup lang="ts">
import type {
	PropertyReelsFeedItemEmits,
	PropertyReelsFeedItemProps
} from './types';
import type { Agent } from '@/core/entities/agent/Agent';
import type { ShortVideoProperty } from '~/core/entities/property';

import { useThrottleFn, useTimeoutFn } from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import {
	getPropertyDetailsPath,
	useShortsContactModalVideoPause,
	useShortsNavigation
} from '~/composables/shorts';
import { usePropertyViewTracking } from '~/composables/usePropertyViewTracking';
import {
	SHORTS_DEFER_LOGIN_FAVORITE_COUNT,
	useToggleFavoriteSync
} from '~/composables/useToggleFavoriteSync';
import { useVideoProgress } from '~/composables/useVideoProgress';
import { getProxiedVideoUrl } from '~/composables/useVideoUrls';
import { useShortsFeedStore } from '~/store/shortsFeedStore';
import { toRaw } from 'vue';

import VideoCardPlayIndicator from '~/components/VideoCard/VideoCardPlayIndicator.vue';
import VideoProgressBar from '~/components/VideoCard/VideoProgressBar.vue';

import IconMaterialSymbolsLightPlayArrow from '~icons/material-symbols-light/play-arrow';

import ReelImageCarousel from './ReelImageCarousel.vue';
import ReelPropertyInfo from './ReelPropertyInfo.vue';
import ReelVideoPlayer from './ReelVideoPlayer.vue';
import {
	buildShortsCtaViewMorePayload,
	buildShortsFavoriteEventData,
	buildShortsFormPropertyClickedPayload,
	buildShortsViewedEntryPayload,
	buildShortsViewedExitPayload
} from './shortsMixpanelPayloads';
import { useShortsEntryContext } from './useShortsEntryContext';
import { useShortsPropertyCardTexts } from './useShortsPropertyCardTexts';

const props = withDefaults(defineProps<PropertyReelsFeedItemProps>(), {
	videoType: 'native',
	isGlobalMuted: true,
	isMobile: true,
	isPreloadOnly: false
});
const emit = defineEmits<PropertyReelsFeedItemEmits>();

const shortsEntry = useShortsEntryContext();
const feedStore = useShortsFeedStore();

const reelVideoPlayerRef = ref<InstanceType<typeof ReelVideoPlayer> | null>(
	null
);
const isVideoLoaded = ref(false);
const isVideoPlaying = ref(false);
const isTapPaused = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const videoDuration = ref(0);
const isTrackingOwner = ref(false);
const isShareAnimating = ref(false);
const isContactModalOpen = ref(false);

const { notifyContactModalOpenChange } = useShortsContactModalVideoPause();
watch(isContactModalOpen, notifyContactModalOpenChange);

const property = computed(() => props.item.property);
const isImageOnly = computed(() => !props.item.videoUrl);
const isAutoplayBlocked = computed(
	() => reelVideoPlayerRef.value?.isAutoplayBlocked ?? false
);
function handleCarouselImageChange(url: string) {
	emit('carousel-image-change', url);
}
const videoUrl = computed(() => {
	if (!props.item.videoUrl) return '';

	const url = props.item.videoUrl;

	// Ensure absolute URL (critical for iOS HLS)
	if (typeof window !== 'undefined' && !url.startsWith('http')) {
		try {
			return new URL(url, window.location.href).href;
		} catch {
			return url;
		}
	}

	return url;
});
const thumbnailUrl = computed(() => props.item.thumbnailUrl);
const images = computed(() => props.item.images ?? []);
const shouldLoadVideo = computed(() => props.shouldLoad && !!videoUrl.value);
const propertyBrokerForContact = computed<Agent | undefined>(() => {
	const agent = property.value.agent;
	if (!agent) return undefined;

	if ('id' in agent && 'profilePicture' in agent) {
		return agent;
	}

	return {
		id: '',
		name: agent.name,
		profilePicture: {
			url: agent.profilePictureUrl,
			croppedUrl: agent.profilePictureUrl
		}
	};
});
const propertyCompanyLogoForContact = computed(() => {
	const company = property.value.company;
	if (!company) return undefined;
	return 'smallLogo' in company ? company.smallLogo : company.logoUrl;
});

const propertyId = computed(() => property.value.id);
const commercialId = computed(() => property.value.commercialId);

const route = useRoute();
const { loggedIn } = useUserSession();
const { navigateToPropertyDetails } = useShortsNavigation();
const {
	trackShortsViewedEntry,
	trackShortsViewedExit,
	trackFormPropertyClicked,
	trackCtaViewMoreClicked
} = useEvents();

const favoriteEventData = computed(() =>
	buildShortsFavoriteEventData({
		property: property.value,
		currentPage: route.fullPath,
		companyName: companyName.value,
		agentName: agentName.value,
		entry: shortsEntry.value
	})
);

const {
	isFavorite,
	isValidData: isFavoriteDataValid,
	handleToggleFavorite: originalHandleToggleFavorite
} = useToggleFavoriteSync(
	propertyId,
	commercialId,
	favoriteEventData,
	'shorts',
	undefined,
	{
		onLoginModalOverlayOpenChange: notifyContactModalOpenChange,
		deferLoginUntilCount: SHORTS_DEFER_LOGIN_FAVORITE_COUNT
	}
);

const isFavoriteMainPulsing = ref(false);
const isFavoriteBackgroundPulsing = ref(false);
const favoritePulseTimeouts = ref<ReturnType<typeof setTimeout>[]>([]);

function clearFavoritePulse() {
	if (!import.meta.client) return;
	favoritePulseTimeouts.value.forEach(id => {
		if (id) clearTimeout(id);
	});
	favoritePulseTimeouts.value = [];
	isFavoriteMainPulsing.value = false;
	isFavoriteBackgroundPulsing.value = false;
}

function runFavoritePulse() {
	if (!import.meta.client) return;

	clearFavoritePulse();

	isFavoriteMainPulsing.value = true;
	const timeout1 = setTimeout(() => {
		if (import.meta.client) {
			isFavoriteMainPulsing.value = false;
		}
	}, 250);
	favoritePulseTimeouts.value.push(timeout1);

	const timeout2 = setTimeout(() => {
		if (import.meta.client) {
			isFavoriteBackgroundPulsing.value = true;
			const timeout3 = setTimeout(() => {
				if (import.meta.client) {
					isFavoriteBackgroundPulsing.value = false;
				}
			}, 350);
			favoritePulseTimeouts.value.push(timeout3);
		}
	}, 500);
	favoritePulseTimeouts.value.push(timeout2);
}

const {
	startTracking,
	stopTracking,
	pauseTracking,
	resumeTracking,
	updateProgress,
	markEnded,
	markClicked,
	markFavorited
} = usePropertyViewTracking();

const { getVideoProgress, saveVideoProgress, clearVideoProgress } =
	useVideoProgress();

const throttledSaveVideoProgress = useThrottleFn(
	(id: string, time: number, dur: number) => saveVideoProgress(id, time, dur),
	1000
);

async function handleToggleFavorite() {
	await originalHandleToggleFavorite();
	if (isFavorite.value) {
		markFavorited();
	}
}

const {
	formattedPrice,
	formattedArea,
	areaLabel,
	roomsInfo,
	region,
	propertyLocation,
	cityState,
	agentName,
	agentFirstName,
	companyName,
	brokerSrc,
	companySrc
} = useShortsPropertyCardTexts(property);

const sharePromoText = 'Confira este imóvel com o PilarHomes Shorts:';

const videoProgress = computed(() =>
	duration.value > 0 ? (currentTime.value / duration.value) * 100 : 0
);

function handlePropertyClick(event?: Event) {
	event?.stopPropagation?.();
	markClicked();
	const url = getPropertyDetailsPath(
		property.value.commercialId,
		property.value.slug
	);
	if (import.meta.client && shortsEntry.value) {
		trackCtaViewMoreClicked(
			buildShortsCtaViewMorePayload({
				property: property.value,
				currentPage: route.fullPath,
				destinationUrl: url,
				entry: shortsEntry.value
			})
		);
	}
	navigateToPropertyDetails(property.value.commercialId, property.value.slug);
}

function handleContactClick(event?: Event) {
	event?.stopPropagation?.();
	// Defer open so the same pointer event is not treated as interact-outside by the dialog
	void nextTick(() => {
		isContactModalOpen.value = true;
	});
}

const { start: hideShareAnimation } = useTimeoutFn(
	() => {
		isShareAnimating.value = false;
	},
	300,
	{ immediate: false }
);

function handleShareFallbackAnimation() {
	isShareAnimating.value = true;
	hideShareAnimation();
}

function getSavedVideoProgress(): number {
	if (!import.meta.client) return 0;

	const savedTime = getVideoProgress(property.value.commercialId);
	if (savedTime && savedTime > 0) {
		return savedTime;
	}

	return 0;
}

function syncSavedProgressIntoVideoElement() {
	const videoEl = reelVideoPlayerRef.value?.videoElementRef;
	if (!videoEl || duration.value <= 0) return;

	const savedProgress = getSavedVideoProgress();
	if (savedProgress > 0 && savedProgress < duration.value) {
		videoEl.currentTime = savedProgress;
	}
}

function playVideo() {
	if (!import.meta.client) return;

	const videoEl = reelVideoPlayerRef.value?.videoElementRef;
	if (!videoEl) return;

	// Only seek when duration is known. If `canplay` ran before `loadedmetadata`,
	// forcing `currentTime = 0` here would wipe a stored resume position before we apply it.
	if (duration.value > 0) {
		const savedProgress = getSavedVideoProgress();
		if (savedProgress > 0 && savedProgress < duration.value) {
			videoEl.currentTime = savedProgress;
		} else {
			videoEl.currentTime = 0;
		}
	}

	reelVideoPlayerRef.value?.play();
}

function togglePlay() {
	if (!import.meta.client) return;

	const videoEl = reelVideoPlayerRef.value?.videoElementRef;
	const player = reelVideoPlayerRef.value;
	if (!videoEl || !player) return;

	if (videoEl.paused) {
		player.play();
	} else {
		player.pause();
	}
}

function handleSeek(ratio: number) {
	const videoEl = reelVideoPlayerRef.value?.videoElementRef;
	if (!videoEl) return;
	videoEl.currentTime = ratio * duration.value;
}

function handleVideoPause() {
	isVideoPlaying.value = false;
	isTapPaused.value = true;
	if (isTrackingOwner.value) {
		pauseTracking();
	}
}

function handleVideoPlaying() {
	isVideoPlaying.value = true;
	// If video is playing, tap pause is no longer active
	isTapPaused.value = false;
	if (isTrackingOwner.value) {
		resumeTracking();
	}
	emit('video-playing');
}

function handleSingleTap() {
	const wasAutoplayBlocked =
		reelVideoPlayerRef.value?.isAutoplayBlocked === true;

	if (wasAutoplayBlocked) {
		reelVideoPlayerRef.value?.userPlay();
	} else {
		togglePlay();
		emit('tap');
	}
}

async function handleDoubleTap() {
	runFavoritePulse();
	if (!isFavorite.value) {
		await handleToggleFavorite();
	}
}

function handleVideoEnded() {
	if (!props.item.isFromHistory) {
		markEnded();
	}
	clearVideoProgress(property.value.commercialId);
	if (props.isActive) {
		const videoEl = reelVideoPlayerRef.value?.videoElementRef;
		const player = reelVideoPlayerRef.value;
		if (videoEl && player) {
			videoEl.currentTime = 0;
			player.play();
		}
	}
}

function handleVideoLoaded() {
	isVideoLoaded.value = true;
	// No flag needed - just check isActive when loadedmetadata arrives
}

function handleLoadedMetadata(d: number) {
	duration.value = d;
	videoDuration.value = d;

	if (props.isActive) {
		syncSavedProgressIntoVideoElement();
		playVideo();
	}
}

watch(
	() => props.shouldLoad,
	shouldLoad => {
		if (!shouldLoad) {
			isVideoLoaded.value = false;
		}
	}
);

watch(
	() => props.isActive,
	isActive => {
		if (isImageOnly.value) {
			if (isActive && !props.item.isFromHistory) {
				startTracking({
					propertyId: property.value.commercialId,
					viewType: 'photos',
					source: 'shorts',
					position: props.index,
					videoData: {
						videoUrl: '',
						thumbnailUrl: props.item.thumbnailUrl,
						propertyTitle: property.value.ad?.title ?? '',
						propertyCommercialId: property.value.commercialId,
						property: toRaw(property.value) as ShortVideoProperty,
						images: props.item.images
					}
				});
				isTrackingOwner.value = true;
			} else if (!isActive && isTrackingOwner.value) {
				stopTracking();
				isTrackingOwner.value = false;
			}
			return;
		}

		if (isActive) {
			if (!props.item.isFromHistory) {
				startTracking({
					propertyId: property.value.commercialId,
					viewType: 'video',
					source: 'shorts',
					position: props.index,
					videoDuration: videoDuration.value || undefined,
					existingViewId: props.item.videoView?.viewId,
					videoData: {
						videoUrl: props.item.videoUrl ?? '',
						thumbnailUrl: props.item.thumbnailUrl,
						propertyTitle: property.value.ad?.title ?? '',
						propertyCommercialId: property.value.commercialId,
						property: toRaw(property.value) as ShortVideoProperty
					}
				});
				isTrackingOwner.value = true;
			}
			playVideo();
		} else {
			reelVideoPlayerRef.value?.pause();
			if (isTrackingOwner.value) {
				stopTracking();
				isTrackingOwner.value = false;
			}
		}
	},
	{ immediate: true }
);

let shortsEntryTime: number | null = null;
let previousEntry: typeof shortsEntry.value = undefined;
let previousIndex: number | null = null;

function fireEntryEvent(
	item: typeof props.item,
	index: number,
	path: string,
	entry: NonNullable<typeof shortsEntry.value>
) {
	const remaining = feedStore.remainingAfter(item.property.commercialId);
	trackShortsViewedEntry(
		buildShortsViewedEntryPayload({
			item: toRaw(item),
			index,
			currentPagePath: path,
			entry,
			recommendationCountVideos: remaining.videos,
			recommendationCountPhotos: remaining.photos
		})
	);
}

watch(
	() => ({
		isActive: props.isActive,
		entry: shortsEntry.value,
		commercialId: props.item.property.commercialId,
		index: props.index
	}),
	(current, previous) => {
		if (!import.meta.client) return;

		const becameInactive = !current.isActive && !!previous?.isActive;
		const propertyChanged =
			current.isActive &&
			!!previous?.isActive &&
			current.commercialId !== previous.commercialId;

		if (
			(becameInactive || propertyChanged) &&
			previousEntry &&
			shortsEntryTime !== null
		) {
			const screenTime = Math.round((Date.now() - shortsEntryTime) / 1000);
			const remaining = feedStore.remainingAfter(
				props.item.property.commercialId
			);
			trackShortsViewedExit(
				buildShortsViewedExitPayload({
					item: toRaw(props.item),
					index: previousIndex ?? props.index,
					currentPagePath: route.fullPath,
					entry: previousEntry,
					screenTime,
					recommendationCountVideos: remaining.videos,
					recommendationCountPhotos: remaining.photos
				})
			);
			shortsEntryTime = null;
			previousEntry = undefined;
			previousIndex = null;
		}

		const becameActive = current.isActive && !previous?.isActive;
		if ((becameActive || propertyChanged) && current.entry) {
			shortsEntryTime = Date.now();
			previousEntry = current.entry;
			previousIndex = current.index;
			fireEntryEvent(props.item, current.index, route.fullPath, current.entry);
		}
	},
	{ immediate: true }
);

watch(isContactModalOpen, isOpen => {
	if (!import.meta.client || !isOpen || !shortsEntry.value) return;
	trackFormPropertyClicked(
		buildShortsFormPropertyClickedPayload({
			property: property.value,
			currentPage: route.fullPath,
			isLoggedIn: loggedIn.value,
			entry: shortsEntry.value
		})
	);
});

// Safety net: on direct URL entry the immediate watcher above fires during
// setup when refs aren't mounted yet, so playVideo() silently fails.
// This watcher catches the case where the video finishes loading while
// already active and ensures playback starts.
watch(isVideoLoaded, loaded => {
	if (!loaded || !props.isActive || !import.meta.client) return;
	nextTick(() => {
		const videoEl = reelVideoPlayerRef.value?.videoElementRef;
		if (videoEl && videoEl.paused && !videoEl.ended) {
			playVideo();
		}
	});
});

onBeforeUnmount(() => {
	if (isContactModalOpen.value) {
		notifyContactModalOpenChange(false);
	}
	clearFavoritePulse();
	if (isTrackingOwner.value) {
		stopTracking();
	}
});
</script>

<template>
	<div
		class="reel-player-container relative h-full w-full bg-transparent"
		data-testid="property-reels-feed-item"
	>
		<!-- Image-only layout: flex column -->
		<div
			v-if="isImageOnly"
			class="relative flex h-full flex-col overflow-hidden bg-transparent md:bg-primary-light-4"
		>
			<div
				class="pointer-events-none absolute inset-0 z-20 shorts-media-overlay xl:hidden"
			/>
			<div class="flex flex-1 items-center justify-center">
				<ReelImageCarousel
					:images="images"
					class="w-full"
					@image-change="handleCarouselImageChange"
				/>
			</div>

			<ReelPropertyInfo
				class="relative inset-auto shrink-0 px-5 pb-[max(16px,env(safe-area-inset-bottom))] xl:px-4 xl:hidden"
				action-buttons-class="right-5"
				:property="property"
				:formatted-price="formattedPrice"
				:formatted-area="formattedArea"
				:area-label="areaLabel"
				:rooms-info="roomsInfo"
				:region="region"
				:city-state="cityState"
				:agent-name="agentName"
				:agent-first-name="agentFirstName"
				:company-name="companyName"
				:broker-src="brokerSrc"
				:company-src="companySrc"
				:is-favorite="isFavorite"
				:is-favorite-data-valid="isFavoriteDataValid"
				:is-favorite-main-pulsing="isFavoriteMainPulsing"
				:is-favorite-background-pulsing="isFavoriteBackgroundPulsing"
				:is-share-animating="isShareAnimating"
				:video-progress="undefined"
				:is-video-loaded="undefined"
				:duration="undefined"
				:show-progress-bar="false"
				@toggle-favorite="handleToggleFavorite"
				@share="handleShareFallbackAnimation"
				@property-click="handlePropertyClick"
				@contact-click="handleContactClick"
				@seek="handleSeek"
			/>
		</div>

		<!-- Video layout: absolute overlay -->
		<template v-else>
			<ReelVideoPlayer
				ref="reelVideoPlayerRef"
				:video-url="videoUrl"
				:thumbnail-url="thumbnailUrl"
				:should-load="shouldLoadVideo"
				:is-active="isActive"
				:is-preload-only="isPreloadOnly"
				:is-muted="isGlobalMuted"
				:index="index"
				@video-loaded="handleVideoLoaded"
				@video-playing="handleVideoPlaying"
				@video-pause="handleVideoPause"
				@video-buffered="emit('video-buffered')"
				@loadedmetadata="handleLoadedMetadata"
				@timeupdate="
					t => {
						currentTime = t;
						if (isActive && !item.isFromHistory) updateProgress(t);
						if (duration > 0)
							throttledSaveVideoProgress(property.commercialId, t, duration);
					}
				"
				@ended="handleVideoEnded"
				@single-tap="handleSingleTap"
				@double-tap="handleDoubleTap"
			/>

			<div
				id="play-button"
				class="pointer-events-none absolute inset-0 z-40 flex items-center justify-center"
			>
				<div
					:class="[
						'flex size-[66px] items-center justify-center rounded-full bg-black transition-all duration-400',
						(isTapPaused && !isVideoPlaying) || isAutoplayBlocked
							? 'scale-100 opacity-100'
							: 'scale-50 opacity-0'
					]"
				>
					<IconMaterialSymbolsLightPlayArrow class="size-11 text-white" />
				</div>
			</div>

			<div
				v-if="isVideoLoaded && duration > 0"
				class="absolute inset-x-3 bottom-3 z-20 hidden xl:block"
			>
				<VideoProgressBar :progress="videoProgress" @seek="handleSeek" />
			</div>

			<!-- Gradient overlay: protects text legibility on mobile/tablet -->
			<div
				class="pointer-events-none absolute inset-0 z-20 shorts-media-overlay xl:hidden"
			/>

			<ReelPropertyInfo
				class="left-5 right-5 flex xl:left-4 xl:right-4 xl:hidden bottom-4"
				:property="property"
				:share-promo-text="sharePromoText"
				:formatted-price="formattedPrice"
				:formatted-area="formattedArea"
				:area-label="areaLabel"
				:rooms-info="roomsInfo"
				:region="region"
				:city-state="cityState"
				:agent-name="agentName"
				:agent-first-name="agentFirstName"
				:company-name="companyName"
				:broker-src="brokerSrc"
				:company-src="companySrc"
				:is-favorite="isFavorite"
				:is-favorite-data-valid="isFavoriteDataValid"
				:is-favorite-main-pulsing="isFavoriteMainPulsing"
				:is-favorite-background-pulsing="isFavoriteBackgroundPulsing"
				:is-share-animating="isShareAnimating"
				:video-progress="videoProgress"
				:is-video-loaded="isVideoLoaded"
				:duration="duration"
				:show-progress-bar="true"
				@toggle-favorite="handleToggleFavorite"
				@share="handleShareFallbackAnimation"
				@property-click="handlePropertyClick"
				@contact-click="handleContactClick"
				@seek="handleSeek"
			/>
		</template>

		<ClientOnly>
			<LazyContactFormModal
				v-model:model-value="isContactModalOpen"
				:property-id="property.id"
				:property-commercial-id="property.commercialId"
				:asking-price="property.askingPrice ?? null"
				:broker="propertyBrokerForContact"
				:company-name="property.company?.name"
				:company-logo="propertyCompanyLogoForContact"
				:analytics-current-page="route.fullPath"
				:video-shorts="(property.featuredVideos?.length ?? 0) > 0"
				context="property"
				:default-expanded="false"
			/>
		</ClientOnly>
	</div>
</template>

<!-- eslint-disable-next-line custom-rules/no-style-tag -->
<style scoped>
.shorts-media-overlay {
	background: linear-gradient(
		0deg,
		rgba(0, 0, 0, 0.4) 19.95%,
		rgba(0, 0, 0, 0) 42.19%,
		rgba(0, 0, 0, 0) 71.85%,
		rgba(0, 0, 0, 0.4) 94.09%
	);
}
</style>
